import { useEffect, useState } from "react";
import { Pause, Play, PiggyBank, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { apiGet, apiPatch } from "@/lib/apiAdmin";

const fmt = (n) => `$${Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;

const Goal = ({ g, act }) => (
  <div className="py-3 space-y-2" data-testid={`goal-${g.goal_id}`}>
    <div className="flex flex-wrap items-center gap-2 text-sm">
      <span className="text-white w-full sm:w-auto">{g.name}</span>
      {g.status !== "active" && <span className="text-[11px] px-2 rounded-full border border-white/15 text-gray-400" data-testid="goal-status">{g.status === "on_hold" ? "on hold" : g.status}</span>}
      <span className="text-xs text-gray-500">{fmt(g.saved)} of {fmt(g.target)} · by {new Date(`${g.target_date}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</span>
      <span className="ml-auto text-xs text-emerald-300" data-testid="goal-per-day">{g.status === "active" ? `${fmt(g.per_day)}/day` : ""}</span>
      {g.status !== "done" && <button onClick={() => act(g, { status: g.status === "on_hold" ? "active" : "on_hold" })} className="p-1 text-gray-500 hover:text-white transition-colors" data-testid="goal-toggle">{g.status === "on_hold" ? <Play className="w-3.5 h-3.5" /> : <Pause className="w-3.5 h-3.5" />}</button>}
      <button onClick={() => window.confirm(`Delete the "${g.name}" goal?`) && act(g, { status: "deleted" })} className="p-1 text-gray-500 hover:text-rose-300 transition-colors" data-testid="goal-delete"><Trash2 className="w-3.5 h-3.5" /></button>
    </div>
    <div className="h-1.5 rounded-full bg-white/5 overflow-hidden"><div className="h-full bg-emerald-400/80 transition-[width] duration-700" style={{ width: `${Math.min(100, g.pct)}%` }} /></div>
    {g.status === "active" && g.behind > 0 && <p className="text-[11px] text-amber-300">{fmt(g.behind)} behind pace</p>}
  </div>
);

export const SavingsGoals = () => {
  const [d, setD] = useState(null);
  const load = () => apiGet("/admin/goals").then(setD).catch(() => {});
  useEffect(() => { load(); }, []);
  const act = async (g, body) => {
    try { await apiPatch(`/admin/goals/${g.goal_id}`, body); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };
  if (!d) return null;
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-5 space-y-2" data-testid="savings-goals">
      <div className="flex flex-wrap items-center gap-3">
        <PiggyBank className="w-4 h-4 text-emerald-400" />
        <p className="text-white font-['Outfit'] flex-1 whitespace-nowrap">Savings goals</p>
        <span className="text-xs text-gray-500 w-full sm:w-auto" data-testid="goals-mode">{d.strictness === "firm" ? "Firm reminders" : "Gentle reminders"} · {d.money_text.enabled ? `owners' text at ${d.money_text.time}` : "daily text off"}</span>
      </div>
      {d.items.length === 0
        ? <p className="text-sm text-gray-500" data-testid="goals-empty">Tell JARVIS on a call or text, e.g. "I'm planning to buy a $2,000 pressure washer by August" — it works out how much to put away each day. Say "put that on hold" anytime.</p>
        : <div className="divide-y divide-white/5">{d.items.map((g) => <Goal key={g.goal_id} g={g} act={act} />)}</div>}
    </div>
  );
};
